import {Component, computed, DestroyRef, effect, OnInit, signal, ViewChild, WritableSignal} from '@angular/core';
import {PanelComponent} from '../../../../../panel/panel.component';
import {BannerSpec} from '../../../../../../core/models/banner-spec.model';
import {AvailableBannerType} from '../../../../../../core/enums/banner/available-banner-type.enum';
import {Step, StepList, StepPanel, StepPanels, Stepper} from 'primeng/stepper';
import {Button} from 'primeng/button';
import {Message} from 'primeng/message';
import {VehicleFacade} from '../../facades/vehicle.facade';
import {AsyncPipe, NgClass} from '@angular/common';
import {FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators} from '@angular/forms';
import {VehicleCategory} from '../../../../category/models/vehicle-category.model';
import {Brand} from '../../../../brand/models/brand.model';
import {VehicleModel} from '../../../../model/model/vehicle-model.model';
import {filter} from 'rxjs';
import {Owner} from '../../../../owners/models/owner.model';
import {DateTime} from 'luxon';
import {
  SelectInputComponent,
  SelectLoadMoreEvent
} from '../../../../../../shared/components/input/select-input/select-input.component';
import {AppTextInputComponent} from '../../../../../../shared/components/input/text-input/text-input.component';
import {AppDateInputComponent} from '../../../../../../shared/components/input/date-input/date-input.component';
import {NgIcon, provideIcons} from '@ng-icons/core';
import {
  tablerCameraPlus,
  tablerCar,
  tablerEngine,
  tablerFolderOpen,
  tablerId,
  tablerMapPin,
  tablerShieldDollar,
  tablerUpload
} from '@ng-icons/tabler-icons';
import {VehicleStorage} from '../../../../storage/model/storage.model';
import {AvailableVehicleUsageType} from '../../../../category/enum/available-vehicle-usage-type.enum';
import {SelectButton} from 'primeng/selectbutton';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {isVehicleRegisterableResolver} from '../../../../../../core/resolvers/is-vehicle-registerable.resolver';
import {ToggleSwitch} from 'primeng/toggleswitch';
import {
  AppNumberInputComponent
} from '../../../../../../shared/components/input/numerical-input/numerical-input.component';
import {resolveVehicleUsageType} from '../../../../../../core/resolvers/vehicle-usage-type.resolver';
import {FileSelectEvent, FileUpload} from 'primeng/fileupload';
import {PrimeTemplate} from 'primeng/api';
import {LoadingComponent} from '../../../../../../shared/components/loading/loading/loading.component';
import {Router} from '@angular/router';
import {AvailableVehicleCategory} from '../../../../category/enum/available-vehicle-category.enum';

export interface VehicleUsageType {
  label: string;
  value: OtherVehiclesAvailableUsageType;
}

export type OtherVehiclesAvailableUsageType = AvailableVehicleUsageType.MILEAGE | AvailableVehicleUsageType.MOTO_HOURS;

@Component({
  selector: 'app-new-vehicle',
  imports: [
    PanelComponent,
    Stepper,
    Step,
    StepList,
    StepPanels,
    StepPanel,
    Button,
    Message,
    AsyncPipe,
    ReactiveFormsModule,
    FormsModule,
    AppTextInputComponent,
    AppDateInputComponent,
    NgClass,
    NgIcon,
    SelectInputComponent,
    SelectButton,
    ToggleSwitch,
    AppNumberInputComponent,
    FileUpload,
    PrimeTemplate,
    LoadingComponent
  ],
  providers: [
    provideIcons({
      tablerCar,
      tablerId,
      tablerShieldDollar,
      tablerFolderOpen,
      tablerEngine,
      tablerMapPin,
      tablerCameraPlus,
      tablerUpload
    })
  ],
  templateUrl: './new-vehicle.component.html',
  styleUrl: './new-vehicle.component.css',
})
export class NewVehicleComponent implements OnInit {
  protected banner: BannerSpec | undefined;

  protected activeStep: number = 1;
  protected selectedImages: File[] = [];
  protected readonly FILES_LIMIT: number = 10;

  @ViewChild('fileUpload')
  protected fileUpload?: FileUpload;

  protected vehicleUsageOptions: VehicleUsageType[] = [
    {label: 'kilometry [km]', value: AvailableVehicleUsageType.MILEAGE},
    {label: 'motogodziny [MH]', value: AvailableVehicleUsageType.MOTO_HOURS}
  ];

  protected readonly vehicleCategory: WritableSignal<VehicleCategory | null> = signal<VehicleCategory | null>(null);
  protected readonly selectedOtherVehiclesUsageType: WritableSignal<AvailableVehicleUsageType> = signal<AvailableVehicleUsageType>(AvailableVehicleUsageType.MILEAGE);
  protected readonly isVehicleRegisterable: WritableSignal<boolean> = signal<boolean>(true);
  protected readonly hasInsurance: WritableSignal<boolean> = signal<boolean>(false);
  protected readonly isAllowedToExtendMileageLimit: WritableSignal<boolean> = signal<boolean>(false);
  protected readonly isAllowedToExtendMotoHoursLimit: WritableSignal<boolean> = signal<boolean>(false);
  protected readonly isVehicleTachographable: WritableSignal<boolean> = signal<boolean>(false);

  private readonly newVehicleForm = new FormGroup({
    vehicleSpecificationSection: new FormGroup({
      category: new FormControl<VehicleCategory | null>(null, Validators.required),
      brand: new FormControl<Brand | null>(null, Validators.required),
      model: new FormControl<VehicleModel | null>(null, Validators.required),
      owner: new FormControl<Owner | null>(null, Validators.required),
      transferDate: new FormControl<DateTime | null>(null, Validators.required),
      transferringPerson: new FormControl<string>('', {nonNullable: true, validators: Validators.required}),
    }),
    vehicleIdentificationSection: new FormGroup({
      registrationPlate: new FormControl<string>('', {nonNullable: true, validators: Validators.nullValidator}),
      vin: new FormControl<string>('', {
        nonNullable: true,
        validators: [Validators.required, Validators.pattern(/^[A-HJ-NPR-Z0-9]{17}$/)]
      }),
      numberAndSeriesOfRegistrationCard: new FormControl<string>('', {
        nonNullable: true,
        validators: Validators.nullValidator
      }),
      factoryNumber: new FormControl<string>('', {nonNullable: true, validators: Validators.nullValidator}),
      identificationNumber: new FormControl<string>('', {nonNullable: true, validators: Validators.nullValidator}),
    }),
    vehicleInsuranceSection: new FormGroup({
      hasInsurance: new FormControl<boolean>(false, Validators.nullValidator),
      insuranceTerminationDate: new FormControl<DateTime | null>(null, Validators.nullValidator),
      ufgInsuranceTerminationDate: new FormControl<DateTime | null>(null, Validators.nullValidator),
    }),
    vehicleServiceSection: new FormGroup({
      technicalInspectionTerminationDate: new FormControl<DateTime | null>(null, Validators.nullValidator),
      tachographInspectionDate: new FormControl<DateTime | null>(null, Validators.nullValidator),
      mileage: new FormControl<number | null>(null),
      motoHours: new FormControl<number | null>(null),
    }),
    vehicleStorageSection: new FormGroup({
      storage: new FormControl<VehicleStorage | null>(null, Validators.required)
    })
  });

  private readonly VEHICLE_MILEAGE_WARN_LIMIT: number = 999999;
  private readonly VEHICLE_MOTOHOURS_WARN_LIMIT: number = 999999;

  public get form(): FormGroup {
    return this.newVehicleForm;
  }

  public get vehicleSpecificationSection(): FormGroup {
    return this.form.get('vehicleSpecificationSection') as FormGroup;
  }

  public get vehicleIdentificationSection(): FormGroup {
    return this.form.get('vehicleIdentificationSection') as FormGroup;
  }

  public get vehicleInsuranceSection(): FormGroup {
    return this.form.get('vehicleInsuranceSection') as FormGroup;
  }

  public get vehicleServiceSection(): FormGroup {
    return this.form.get('vehicleServiceSection') as FormGroup;
  }

  public get vehicleStorageSection(): FormGroup {
    return this.form.get('vehicleStorageSection') as FormGroup;
  }

  constructor(
    protected readonly vehicleFacade: VehicleFacade,
    private readonly destroyRef: DestroyRef,
    private readonly router: Router
  ) {
    this.registerIsRegisterableCallback();
    this.registerVehicleRegisterableCallback();
    this.registerIsTachographableVehicleCategoryCallback();
  }

  ngOnInit(): void {
    this.constructBanner();

    this.vehicleFacade.fetchVehicleCategories();
    this.vehicleFacade.fetchVehicleBrands();

    this.registerVehicleModelSubscription();
    this.registerVehicleCategorySubscription();
    this.registerHasInsuranceSubscription();
    this.registerVehicleMileageSubscription();
    this.registerVehicleMotoHoursSubscription();
  }

  protected onVehicleOwnerLazyLoad(lazyLoadEvent: SelectLoadMoreEvent): void {
    this.vehicleFacade.fetchVehicleOwners(lazyLoadEvent);
  }

  protected onVehicleStoragesLazyLoad(lazyLoadEvent: SelectLoadMoreEvent): void {
    this.vehicleFacade.fetchVehicleStorages(lazyLoadEvent)
  }

  protected onSubmit() {
    this.vehicleFacade.createNewVehicle({
      ...this.form.value,
      files: this.selectedImages
    })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.clearData();
          this.router.navigate(['/vehicles/all'])
        }
      });
  }

  protected clearData(): void {
    this.form.reset()

    this.vehicleCategory.set(null);
    this.selectedOtherVehiclesUsageType.set(AvailableVehicleUsageType.MILEAGE);
    this.isVehicleRegisterable.set(true);
    this.hasInsurance.set(false);
    this.isAllowedToExtendMileageLimit.set(false);
    this.isAllowedToExtendMotoHoursLimit.set(false);
    this.activeStep = 1;
    this.selectedImages = [];
    this.fileUpload?.clear();
  }

  protected readonly vehicleUsageTypeComputed = computed(() => {
    const category = this.vehicleCategory();

    return category ? resolveVehicleUsageType(category) : null;
  });

  protected readonly ifOtherVehicleHasMileageUsageTypeComputed = computed(() => {
    return this.selectedOtherVehiclesUsageType() === AvailableVehicleUsageType.MILEAGE;
  })


  protected get mileageVehicleUsageType(): AvailableVehicleUsageType {
    return AvailableVehicleUsageType.MILEAGE;
  }

  protected get motoHoursVehicleUsageType(): AvailableVehicleUsageType {
    return AvailableVehicleUsageType.MOTO_HOURS;
  }

  protected get bothVehicleUsageType(): AvailableVehicleUsageType {
    return AvailableVehicleUsageType.BOTH;
  }

  private registerIsRegisterableCallback(): void {
    effect(() => {
      const isRegisterable = this.isVehicleRegisterable();

      const registrationPlateControl = this.vehicleIdentificationSection.controls['registrationPlate'];
      const numberAndSeriesOfRegistrationCardControl = this.vehicleIdentificationSection.controls['numberAndSeriesOfRegistrationCard'];
      const vin = this.vehicleIdentificationSection.controls['vin'];

      if (isRegisterable) {
        vin.setValidators([Validators.required, Validators.pattern(/^[A-HJ-NPR-Z0-9]{17}$/)]);
      } else {
        registrationPlateControl.setValue('');
        numberAndSeriesOfRegistrationCardControl.setValue('');
        vin.setValidators([Validators.nullValidator]);
        vin.setValue('');
      }

      registrationPlateControl.updateValueAndValidity();
      vin.updateValueAndValidity();
    });
  }

  private registerVehicleRegisterableCallback(): void {
    effect(() => {
      const category = this.vehicleCategory();
      if (category) this.isVehicleRegisterable.set(isVehicleRegisterableResolver(category.category));
    })
  }

  private registerIsTachographableVehicleCategoryCallback(): void {
    effect(() => {
      const vehicleCategory = this.vehicleCategory();

      if (vehicleCategory?.category === AvailableVehicleCategory.TRUCK || vehicleCategory?.category === AvailableVehicleCategory.DELIVERY) {
        this.isVehicleTachographable.set(true);
      } else {
        this.isVehicleTachographable.set(false);
      }
    })
  }

  private constructBanner(): void {
    this.banner = {type: AvailableBannerType.INFO, message: 'Formularz dodawania nowego pojazdu'};
  }

  private registerVehicleCategorySubscription(): void {
    this.vehicleSpecificationSection.controls['category'].valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(category => {
        this.vehicleCategory.set(category);
      });
  }

  private registerVehicleModelSubscription(): void {
    this.newVehicleForm.controls.vehicleSpecificationSection.controls.brand.valueChanges
      .pipe(filter((brand): brand is Brand => brand !== null))
      .subscribe(brand => {
        this.vehicleFacade.fetchVehicleModels(brand.id);
      });
  }

  private registerVehicleMileageSubscription(): void {
    const control = this.vehicleServiceSection.controls['mileage'];

    control.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(mileage => {
        if (mileage > this.VEHICLE_MILEAGE_WARN_LIMIT && !this.isAllowedToExtendMileageLimit()) {
          this.vehicleFacade.displayVehicleUsageConfirmationDialog()
            .subscribe(confirmed => {
              if (confirmed?.accepted) {
                control.clearValidators();
                this.isAllowedToExtendMileageLimit.set(true);
                this.isAllowedToExtendMotoHoursLimit.set(false);
              } else {
                control.addValidators(Validators.max(this.VEHICLE_MILEAGE_WARN_LIMIT));
              }

              control.updateValueAndValidity();
              control.patchValue(control.value, {emitEvent: false});
              control.markAsTouched();
            });
        }
      });
  }

  private registerVehicleMotoHoursSubscription(): void {
    const control = this.vehicleServiceSection.controls['motoHours'];

    control.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(mileage => {
        if (mileage > this.VEHICLE_MOTOHOURS_WARN_LIMIT && !this.isAllowedToExtendMotoHoursLimit()) {
          this.vehicleFacade.displayVehicleUsageConfirmationDialog()
            .subscribe(confirmed => {
              if (confirmed?.accepted) {
                control.clearValidators();
                this.isAllowedToExtendMileageLimit.set(false);
                this.isAllowedToExtendMotoHoursLimit.set(true);
              } else {
                control.addValidators(Validators.max(this.VEHICLE_MOTOHOURS_WARN_LIMIT));
              }

              control.updateValueAndValidity();
              control.patchValue(control.value, {emitEvent: false});
              control.markAsTouched();
            });
        }
      });
  }

  private registerHasInsuranceSubscription(): void {
    this.vehicleInsuranceSection.controls['hasInsurance'].valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(hasInsurance => this.hasInsurance.set(hasInsurance));
  }

  protected onSelect(event: FileSelectEvent): void {
    event.currentFiles.splice(this.FILES_LIMIT);
    this.selectedImages = [...event.currentFiles];
  }

  protected onClearFiles(): void {
    this.selectedImages = [];
  }
}
